import React, { useEffect, useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import {
  tokens,
  fmtMoney,
  SignalBar,
  StatusPill,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import DenialDetail from "../components/DenialDetail";
import {
  getDenials,
  fileAppeal,
  recordOutcome,
  buildDenialSignals,
} from "../services/denialsService";
import { DENIAL_STATUS, DENIAL_STATUS_META } from "../common/constants";

/**
 * Denials Management & Appeals (Módulo G) — master-detail:
 * izquierda worklist priorizada por monto, derecha detalle + appeal tracking.
 */
export default function DenialsManagementPage() {
  const [denials, setDenials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    getDenials().then((rows) => {
      if (!active) return;
      setDenials(rows);
      setSelectedId(rows[0]?.id ?? null);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const selected = useMemo(
    () => denials.find((d) => d.id === selectedId) || null,
    [denials, selectedId]
  );

  const signals = useMemo(() => {
    const s = buildDenialSignals(denials);
    return [
      { label: "$ denied MTD", value: fmtMoney(s.deniedMtd), tone: "crit", hint: "Total denied amount month-to-date (835 remits)" },
      { label: "Recovery rate", value: `${s.recoveryRate}%`, tone: s.recoveryRate >= 50 ? "ok" : "warn", hint: "Recovered / denied, month-to-date" },
      { label: "Avg days to appeal", value: s.avgDaysToAppeal, tone: s.avgDaysToAppeal <= 10 ? "ok" : "warn" },
      { label: "Top CARC", value: s.topCarc, tone: "info", hint: "Denial reason with the highest $ in the worklist" },
      {
        label: "Open denials",
        value: denials.filter(
          (d) => d.status === DENIAL_STATUS.NEW || d.status === DENIAL_STATUS.IN_APPEAL
        ).length,
        tone: "warn",
      },
    ];
  }, [denials]);

  const handleFileAppeal = async (claimId) => {
    setSaving(true);
    // Optimistic UI — cambio solo local hasta que exista
    // POST /api/billing/denials/{claimId}/appeal en el backend.
    setDenials((prev) =>
      prev.map((d) =>
        d.id === claimId
          ? { ...d, status: DENIAL_STATUS.IN_APPEAL, appealFiledDate: dayjs().format("YYYY-MM-DD") }
          : d
      )
    );
    await fileAppeal(claimId);
    setSaving(false);
  };

  const handleRecordOutcome = async (claimId, outcome) => {
    setSaving(true);
    // Optimistic UI — cambio solo local hasta que exista
    // PUT /api/billing/denials/{claimId}/outcome en el backend.
    setDenials((prev) =>
      prev.map((d) =>
        d.id === claimId
          ? {
              ...d,
              status: outcome,
              outcome:
                outcome === DENIAL_STATUS.RECOVERED
                  ? "Recovered — recorded by user"
                  : "Written off — recorded by user",
            }
          : d
      )
    );
    await recordOutcome(claimId, outcome);
    setSaving(false);
  };

  const columns = [
    { key: "id", header: "Claim", mono: true, width: 140 },
    { key: "payer", header: "Payer" },
    {
      key: "carc",
      header: "CARC",
      mono: true,
      render: (row) => (
        <Tooltip title={row.carcReason} arrow>
          <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 13, color: tokens.crit }}>
            {row.carc}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      mono: true,
      render: (row) => fmtMoney(row.amount),
    },
    { key: "ageDays", header: "Age", align: "right", mono: true, render: (row) => `${row.ageDays}d` },
    {
      key: "appealDeadline",
      header: "Deadline",
      render: (row) => {
        const days = dayjs(row.appealDeadline).diff(dayjs(), "day");
        const tone = days < 0 ? "muted" : days <= 7 ? "crit" : days <= 21 ? "warn" : "ok";
        return <StatusPill tone={tone} label={days < 0 ? "Expired" : `${days}d`} dense />;
      },
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const meta = DENIAL_STATUS_META[row.status];
        return <StatusPill tone={meta.tone} label={meta.label} dense />;
      },
    },
  ];

  return (
    <ClinicalPageShell
      title="Denials Management"
      subtitle="Prioritized denial worklist from 835 remits: classify root cause, file appeals before deadline and track recovery."
      moduleTag="Module G"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ flex: "0 1 45%", minWidth: 420 }}>
          <ClinicalDataGrid
            columns={columns}
            rows={denials}
            rowKey={(row) => row.id}
            loading={loading}
            onRowClick={(row) => setSelectedId(row.id)}
            isRowSelected={(row) => row.id === selectedId}
            emptyMessage="No denials in the worklist for the current period."
            maxHeight={640}
          />
        </Box>
        <Box sx={{ flex: "1 1 480px", minWidth: 380 }}>
          <DenialDetail
            denial={selected}
            onFileAppeal={handleFileAppeal}
            onRecordOutcome={handleRecordOutcome}
            saving={saving}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
